import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  TextField,
  Paper,
  Grid,
  FormLabel,
  Switch,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import { AuthorizedLayout } from "../Layouts/AuthorizedLayout";
import { useAppDispatch } from "../../Store/hooks";
import { showLoader } from "../../Store/loaderSlice";
import {
  GetApiMethod,
  PostApiMethod,
  PutApiMethod,
} from "../../Utils/ApiService";
import { useParams } from "react-router-dom";
import { SportsListDto } from "./sports";
import { showToaster } from "../../Store/toastSlice";
import { DisplayErrorMessages } from "../../Utils/HelperService";
import { CustomModal, ModalStyle } from "../../Components/ModalStyle";
import { Form, Formik } from "formik";
import { sportSchema } from "../HomePages/AddUserSchema";

// ✅ Define a nested structure type (recursive)
export interface StructuredPosition extends SportsListDto {
  children: StructuredPosition[]; // nested children of same structure
}

interface TreeNodeProps {
  node: StructuredPosition;
  allNodes: StructuredPosition[];
  depth?: number;
  onAdd: (parentId: string) => void;
  onUpdate: (id: string, name: string) => void;
  onDelete: (id: number) => void;
}

const TreeNode: React.FC<TreeNodeProps> = ({
  node,
  allNodes,
  depth = 0,
  onAdd,
  onUpdate,
  onDelete,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const children = node.children ?? [];

  return (
    <Box sx={{ ml: depth * 3, mt: 1 }}>
      <Paper
        elevation={1}
        sx={{
          p: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          backgroundColor: "#f9f9f9",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
          {children.length > 0 ? (
            <IconButton size="small" onClick={() => setIsExpanded(!isExpanded)}>
              {isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
            </IconButton>
          ) : (
            <Box sx={{ width: 32 }} />
          )}

          <Typography
            variant="body1"
            sx={{ ml: 1, flexGrow: 1, fontWeight: 500 }}
          >
            {node.name}
          </Typography>
        </Box>

        <Box>
          <IconButton
            size="small"
            color="primary"
            onClick={() => {
              onAdd(node.id);
            }}
          >
            <AddIcon />
          </IconButton>
          <IconButton
            size="small"
            color="secondary"
            onClick={() => {
              onUpdate(node.id, node.name);
            }}
          >
            <EditIcon />
          </IconButton>
        </Box>
      </Paper>

      {isExpanded &&
        children.map((child) => (
          <TreeNode
            key={child.id}
            node={child}
            allNodes={allNodes}
            depth={depth + 1}
            onAdd={onAdd}
            onUpdate={onUpdate}
            onDelete={onDelete}
          />
        ))}
    </Box>
  );
};

const SportsDetail: React.FC = () => {
  const dispatch = useAppDispatch();
  const { id } = useParams(); // "123"
  const [sportsDetail, setSportsDetail] = useState<SportsListDto | null>(null);
  const [position, setPosition] = useState<StructuredPosition[]>([]);
  const [formModal, setFormModal] = useState(false);
  const initialValue: SportsListDto = {
    name: "",
    id: "",
    isSports: false,
    parentId: "",
    subParentId: "",
    status: true,
  };

  const [sportsData, setSportsData] = useState<SportsListDto>(initialValue);

  function buildNestedStructure(items: SportsListDto[]): StructuredPosition[] {
    const itemMap = new Map<string, StructuredPosition>();
    const roots: StructuredPosition[] = []; // ✅ Declare roots before use

    // Step 1: Map all items by id
    items.forEach((item) => {
      itemMap.set(item.id, { ...item, children: [] });
    });

    // Step 2: Build hierarchy
    items.forEach((item) => {
      if (item.subParentId) {
        const parent = itemMap.get(item.subParentId);
        if (parent) {
          parent.children.push(itemMap.get(item.id)!);
        }
      } else {
        roots.push(itemMap.get(item.id)!);
      }
    });

    return roots;
  }

  const getSportsDetail = useCallback(
    (id: String) => {
      dispatch(showLoader({ showLoader: true }));
      GetApiMethod("sports/all/" + id)
        .then((res) => {
          setSportsDetail(res.data.data);
          const positions: SportsListDto[] = res.data.data.others;
          setPosition(buildNestedStructure(positions));
          dispatch(showLoader({ showLoader: false }));
        })
        .catch(() => {
          dispatch(showLoader({ showLoader: false }));
        });
    },
    [dispatch]
  );

  const createSportsPosition = useCallback(
    async (data: SportsListDto) => {
      try {
        dispatch(showLoader({ showLoader: true }));
        if (!data.subParentId) {
          delete data.subParentId;
        }
        const apiMethod = data.id
          ? PutApiMethod("sports/" + data.id, data)
          : PostApiMethod("sports", data);
        const res = await apiMethod;
        dispatch(showLoader({ showLoader: false }));
        getSportsDetail(id!);
        setFormModal(false);
        dispatch(
          showToaster({
            dialogBgColor: "success",
            dialogMsg: res.data.message,
            showDialog: true,
            timer: "5000",
          })
        );
      } catch (error) {
        dispatch(showLoader({ showLoader: false }));
        const errorMsg = DisplayErrorMessages(error);

        dispatch(
          showToaster({
            dialogBgColor: "bg-danger",
            dialogMsg: errorMsg,
            showDialog: true,
            timer: "5000",
          })
        );
      }
    },
    [dispatch, getSportsDetail, id]
  );

  const addNode = useCallback(
    (subParent: string) => {
      setSportsData({
        id: "",
        parentId: id!,
        subParentId: subParent,
        name: "",
        isSports: false,
        status: true,
      });
      setFormModal(true);
    },
    [id]
  );

  const updateNode = useCallback((id: string, name: string) => {
    setSportsData({
      id: id,
      parentId: id!,
      subParentId: "",
      name: name,
      isSports: false,
      status: true,
    });
    setFormModal(true);
  }, []);

  useEffect(() => {
    if (id) {
      getSportsDetail(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  return (
    <AuthorizedLayout>
      <Box sx={{ p: 4 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography variant="h5" fontWeight={600}>
            {sportsDetail?.name}
          </Typography>
          <button
            className="btn btn-danger"
            onClick={() => {
              addNode("");
            }}
          >
            <AddIcon /> Add Position
          </button>
        </Box>

        <Box>
          {position.map((node) => (
            <TreeNode
              key={node.id}
              node={node}
              allNodes={position}
              onAdd={addNode}
              onUpdate={updateNode}
              onDelete={() => {}}
            />
          ))}
        </Box>
        <CustomModal
          open={formModal}
          onClose={() => {
            setFormModal(false);
          }}
          header={`${sportsData?.id ? "Edit" : "Add"} Position`}
          style={ModalStyle({ width: 600, height: "auto" })}
        >
          <Formik
            initialValues={sportsData}
            validationSchema={sportSchema}
            onSubmit={async (values) => {
              createSportsPosition(values);
            }}
            enableReinitialize={true}
          >
            {({
              handleSubmit,
              handleChange,
              values,
              errors,
              touched,
              setFieldValue,
            }) => (
              <>
                <Box className="modal-body">
                  <Form onSubmit={handleSubmit}>
                    <Grid item md={12} m={2}>
                      <Grid container spacing={1} className="p-3">
                        <FormLabel className="fw-bold">
                          Sports Position:<sup className="text-danger">*</sup>
                        </FormLabel>
                        <TextField
                          className="w-100"
                          placeholder="Type here..."
                          variant="standard"
                          value={values.name}
                          name="name"
                          onChange={handleChange}
                        />
                        {touched.name && errors.name && (
                          <div className="text-danger">{errors.name}</div>
                        )}
                      </Grid>
                      {sportsData?.id && (
                        <Grid
                          container
                          spacing={1}
                          alignItems={"center"}
                          className="p-3"
                        >
                          <FormLabel
                            className="fw-bold"
                            style={{ marginRight: 8 }}
                          >
                            Status:
                          </FormLabel>
                          <Switch
                            name="status"
                            checked={values.status} // true = Active, false = InActive
                            onChange={handleChange}
                            color="primary"
                            disabled={values.id === "" ? true : false}
                          />
                        </Grid>
                      )}
                    </Grid>

                    <Box className="d-flex justify-content-end mt-3 p-3">
                      <button className="btn btn-danger" type="submit">
                        Submit
                      </button>
                    </Box>
                  </Form>
                </Box>
              </>
            )}
          </Formik>
        </CustomModal>
      </Box>
    </AuthorizedLayout>
  );
};

export default SportsDetail;
